import { useMutation } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import type {
    TApiODIN,
    TNewProject,
    TExtractionStatus,
    TFileUploadResponse,
    TProjectNameCheckInfo,
    TPropertyAddressCheckInfo,
    TPropertyNameCheckInfo,
    TProjectInfo,
    TNewProperty,
    TMultipropPropertyInfo,
} from "@/apis/odin/apiODIN";
import type { TSliceNotifications } from "@/store/notifications/sliceNotifications";
import { extractionKeys, projectKeys } from "@/queries/queryKeys";
import { apiErrorDetail } from "@/apis/apiError";
import { chunkIdArray } from "@/utils/chunk";
import { isProjectNotEmptyError } from "./isProjectNotEmptyError";

export const createProjectMutationHooks = ({
    apiODIN,
    queryClient,
    addNotificationWithDefaultTimeout,
}: {
    apiODIN: TApiODIN;
    queryClient: QueryClient;
    addNotificationWithDefaultTimeout: TSliceNotifications["addNotificationWithDefaultTimeout"];
}) => {
    const invalidateProjectQueries = () =>
        queryClient.invalidateQueries({
            queryKey: projectKeys.all,
        });

    const invalidateExtractionQueries = () =>
        queryClient.invalidateQueries({
            queryKey: extractionKeys.all,
        });

    const useDeleteFilesMutation = () =>
        useMutation({
            mutationFn: async ({
                projectId,
                databaseId,
                fileIds,
            }: {
                projectId: number;
                databaseId: number;
                fileIds: number[];
            }) => {
                const chunks = chunkIdArray(fileIds);
                for (const chunk of chunks) {
                    await apiODIN.deleteFiles({
                        projectId,
                        databaseId,
                        fileIds: chunk,
                    });
                }
            },
            onSuccess: () => {
                addNotificationWithDefaultTimeout({
                    title: "Files deleted successfully",
                    kind: "toast",
                    severity: "success",
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Files deletion failed",
                    kind: "toast",
                    severity: "danger",
                });
            },
            onSettled: async () => {
                await invalidateProjectQueries();
            },
        });

    const useDeleteExtractionsMutation = () =>
        useMutation({
            mutationFn: async ({
                projectId,
                databaseId,
                extractionIds,
            }: {
                projectId: number;
                databaseId: number;
                extractionIds: number[];
            }) => {
                const chunks = chunkIdArray(extractionIds);
                for (const chunk of chunks) {
                    await apiODIN.deleteExtractions({
                        extractionIds: chunk,
                        projectId,
                        databaseId,
                    });
                }
            },
            onSuccess: () => {
                addNotificationWithDefaultTimeout({
                    title: "Extractions deleted successfully",
                    kind: "toast",
                    severity: "success",
                });
            },
            onError: (error: unknown) => {
                addNotificationWithDefaultTimeout({
                    title: "Extractions deletion failed",
                    message: apiErrorDetail(error) ?? undefined,
                    kind: "toast",
                    severity: "danger",
                });
            },
            onSettled: async () => {
                await Promise.all([
                    invalidateProjectQueries(),
                    invalidateExtractionQueries(),
                ]);
            },
        });

    const useDeleteProjectMutation = () =>
        useMutation({
            mutationFn: async ({
                projectId,
                databaseId,
            }: {
                projectId: number;
                databaseId: number;
            }) => {
                await apiODIN.deleteDeal({ projectId, databaseId });
            },
            onSuccess: async () => {
                await Promise.all([
                    invalidateProjectQueries(),
                    invalidateExtractionQueries(),
                ]);
                addNotificationWithDefaultTimeout({
                    title: "Project successfully deleted",
                    kind: "toast",
                    severity: "success",
                });
            },
            onError: (error: unknown) => {
                const isNotEmpty = isProjectNotEmptyError(error);
                addNotificationWithDefaultTimeout({
                    title: isNotEmpty
                        ? "Project deletion not possible - project not empty"
                        : "Project deletion failed",
                    message: isNotEmpty
                        ? "Only empty projects can be deleted. Remove the files in this project first."
                        : undefined,
                    kind: "toast",
                    severity: isNotEmpty ? "warning" : "danger",
                });
            },
        });

    const useCreateProjectMutation = () =>
        useMutation({
            mutationFn: async (newProject: TNewProject) => {
                const response = await apiODIN.multipropCreateDeal(newProject);
                return response.data;
            },
            onSuccess: async data => {
                await queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
                addNotificationWithDefaultTimeout({
                    title: "Project successfully created",
                    message:
                        data.createdPropertyNames.length > 0
                            ? [
                                  "Created properties:",
                                  ...data.createdPropertyNames,
                              ].join("\n- ")
                            : undefined,
                    severity: "success",
                    kind: "toast",
                });
                if (data.failedPropertyNames.length > 0) {
                    addNotificationWithDefaultTimeout({
                        title: "Not all properties could be created",
                        message: [
                            "Failed to create properties:",
                            ...data.failedPropertyNames,
                        ].join("\n- "),
                        severity: "danger",
                        kind: "toast",
                    });
                }
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to create project",
                    severity: "danger",
                    kind: "toast",
                });
            },
        });

    const useUpdateExtractionMutation = () =>
        useMutation({
            mutationFn: async ({
                extractionId,
                extractionStatus,
                activePeriods,
            }: {
                projectId: number;
                databaseId: number;
                extractionId: number;
                extractionStatus: TExtractionStatus;
                activePeriods?: {
                    extractionId: number;
                    periodId: number;
                }[];
            }) => {
                const response = await apiODIN.updateExtraction({
                    extractionId,
                    extractionStatus,
                    activePeriods,
                });
                return response.data;
            },
            onSuccess: async (_data, { extractionId }) => {
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: projectKeys.all,
                        refetchType: "none",
                    }),
                    queryClient.invalidateQueries({
                        queryKey: extractionKeys.raw(extractionId),
                        refetchType: "none",
                    }),
                    queryClient.invalidateQueries({
                        queryKey: extractionKeys.standardized(extractionId),
                        refetchType: "none",
                    }),
                    queryClient.invalidateQueries({
                        queryKey:
                            extractionKeys.overlappingPeriods(extractionId),
                        refetchType: "none",
                    }),
                ]);
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to update extraction",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useAddProjectFileMutation = () =>
        useMutation({
            mutationFn: async ({
                projectId,
                databaseId,
                file,
            }: {
                projectId: number;
                databaseId: number;
                file: File | Blob;
            }): Promise<TFileUploadResponse> => {
                const response = await apiODIN.addDealFile({
                    projectId,
                    databaseId,
                    file,
                });
                return response.data;
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
        });

    const useProjectNameCheckMutation = () =>
        useMutation({
            mutationFn: async (info: TProjectNameCheckInfo) => {
                const response = await apiODIN.projectNameCheck(info);
                return response.data;
            },
        });

    const usePropertyAddressCheckMutation = () =>
        useMutation({
            mutationFn: async (info: TPropertyAddressCheckInfo) => {
                const response = await apiODIN.propertyAddressCheck(info);
                return response.data;
            },
        });

    const usePropertyNameCheckMutation = () =>
        useMutation({
            mutationFn: async (info: TPropertyNameCheckInfo) => {
                const response = await apiODIN.propertyNameCheck(info);
                return response.data;
            },
        });

    const useDownloadOriginalFilesMutation = () =>
        useMutation({
            mutationFn: async ({
                fileIds,
                databaseId,
                projectId,
            }: {
                fileIds: number[];
                databaseId: number;
                projectId: number;
            }) => {
                const fileIdsDistinct = Array.from(new Set(fileIds));
                if (
                    fileIdsDistinct.length === 1 &&
                    fileIdsDistinct[0] !== undefined
                ) {
                    const response = await apiODIN.downloadOriginalFile({
                        fileId: fileIdsDistinct[0],
                        databaseId,
                        projectId,
                    });
                    return [response];
                }
                const chunks = chunkIdArray(fileIdsDistinct);
                return Promise.all(
                    chunks.map(chunk =>
                        apiODIN.downloadOriginalFilesZip({
                            fileIds: chunk,
                            databaseId,
                            projectId,
                        }),
                    ),
                );
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to download original files",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useUpdateProjectMutation = () =>
        useMutation({
            mutationFn: async (deal: TProjectInfo) => {
                const response = await apiODIN.multipropUpdateDeal(deal);
                return response.data;
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to update project",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useAddPropertyMutation = () =>
        useMutation({
            mutationFn: async (newProperty: TNewProperty) => {
                const response =
                    await apiODIN.multipropCreateProperty(newProperty);
                return response.data;
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to add property",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useUpdatePropertyMutation = () =>
        useMutation({
            mutationFn: async ({
                property,
                projectId,
                databaseId,
            }: {
                property: TMultipropPropertyInfo;
                projectId: number;
                databaseId: number;
            }) => {
                await apiODIN.multipropUpdateProperty(
                    property,
                    projectId,
                    databaseId,
                );
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to update property",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    const useDeletePropertyMutation = () =>
        useMutation({
            mutationFn: async ({
                propertyId,
                databaseId,
                projectId,
            }: {
                propertyId: number;
                databaseId: number;
                projectId: number;
            }) => {
                await apiODIN.multipropDeleteProperty({
                    propertyId,
                    databaseId,
                    projectId,
                });
            },
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: projectKeys.all,
                });
            },
            onError: () => {
                addNotificationWithDefaultTimeout({
                    title: "Failed to delete property",
                    kind: "toast",
                    severity: "danger",
                });
            },
        });

    return {
        useDeleteFilesMutation,
        useDeleteExtractionsMutation,
        useDeleteProjectMutation,
        useCreateProjectMutation,
        useUpdateExtractionMutation,
        useAddProjectFileMutation,
        useProjectNameCheckMutation,
        usePropertyAddressCheckMutation,
        usePropertyNameCheckMutation,
        useDownloadOriginalFilesMutation,
        useUpdateProjectMutation,
        useAddPropertyMutation,
        useUpdatePropertyMutation,
        useDeletePropertyMutation,
    };
};

export type TProjectMutationHooks = ReturnType<
    typeof createProjectMutationHooks
>;
